/**
 * Shared gate for files entering a roll's upload queue, whether they arrive via
 * the OS file picker or a drag-and-drop.
 *
 * The `accept` attribute on `<input type="file">` only ever constrained the
 * picker dialog — a drop handled off `dataTransfer` bypasses it entirely. And
 * nothing downstream re-checks: the upload PUT hardcodes `Content-Type:
 * image/tiff`, so a non-TIFF that slips through is stored as a TIFF and
 * surfaces only as a scan permanently stuck on "Processing".
 */

/** Extensions the scan pipeline can actually process. */
export const ACCEPTED_EXTENSIONS = ['.tif', '.tiff'] as const;

/** Value for the file input's `accept` attribute. */
export const ACCEPT_ATTRIBUTE = ACCEPTED_EXTENSIONS.join(',');

export interface AcceptanceResult {
  /** Files that passed both the extension check and the duplicate check. */
  accepted: File[];
  /** Names of files rejected for having the wrong extension. */
  rejectedNames: string[];
  /** How many files were dropped as already-queued duplicates. */
  duplicateCount: number;
}

/**
 * Identity of a file for duplicate detection — name alone is too weak.
 *
 * NUL separates the fields because it is the one character a filename cannot
 * contain, so no name can spoof a field boundary. Written as an escape rather
 * than a literal byte so this stays a text file git can diff.
 */
function fileKey(file: File): string {
  return `${file.name}\u0000${file.size}\u0000${file.lastModified}`;
}

export function hasAcceptedExtension(filename: string): boolean {
  const lower = filename.toLowerCase();
  return ACCEPTED_EXTENSIONS.some((ext) => lower.endsWith(ext));
}

/**
 * Splits incoming files into those that should join the queue and those that
 * should not, deduplicating against files already queued.
 *
 * Duplicates are dropped rather than rejected loudly: re-dropping a batch is an
 * easy slip, and queuing the same file twice would create two scan records for
 * one negative.
 */
export function acceptFiles(incoming: File[], alreadyQueued: File[] = []): AcceptanceResult {
  const seen = new Set(alreadyQueued.map(fileKey));
  const accepted: File[] = [];
  const rejectedNames: string[] = [];
  let duplicateCount = 0;

  for (const file of incoming) {
    if (!hasAcceptedExtension(file.name)) {
      rejectedNames.push(file.name);
      continue;
    }
    const key = fileKey(file);
    // Guards against duplicates within a single drop as well as against the
    // existing queue, since `seen` grows as we go.
    if (seen.has(key)) {
      duplicateCount += 1;
      continue;
    }
    seen.add(key);
    accepted.push(file);
  }

  return { accepted, rejectedNames, duplicateCount };
}

/** Human-readable summary of what was turned away, or null if nothing was. */
export function describeRejections(result: AcceptanceResult): string | null {
  const parts: string[] = [];

  if (result.rejectedNames.length > 0) {
    const shown = result.rejectedNames.slice(0, 3).join(', ');
    const extra = result.rejectedNames.length - 3;
    parts.push(
      `${result.rejectedNames.length} file${result.rejectedNames.length === 1 ? '' : 's'} skipped ` +
        `(only ${ACCEPTED_EXTENSIONS.join(' and ')} accepted): ${shown}${extra > 0 ? ` and ${extra} more` : ''}`
    );
  }

  if (result.duplicateCount > 0) {
    parts.push(
      `${result.duplicateCount} file${result.duplicateCount === 1 ? '' : 's'} already queued`
    );
  }

  return parts.length > 0 ? parts.join('. ') : null;
}
